import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "@/components/motion/Reveal";

const ease = [0.16, 1, 0.3, 1];

// Positions in % of the image; card anchor is its top-left corner.
const SPOTS = [
  { dotM: [69.3, 46.5], dot: [69.3, 45.9], card: [56, 15.5], img: "/review-thumb-flushboxx.webp" },
  { dotM: [58.7, 75.6], dot: [58.7, 80.2], card: [74, 72], img: "/review-thumb-mounting.webp" },
  { dotM: [50.4, 62.5], dot: [50.4, 64.7], card: [13, 50], img: "/review-thumb-stands.webp" },
];

const Stars = ({ size = "w-3 h-3" }) => (
  <span className="flex gap-0.5">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className={size} style={{ fill: "#DB960D", color: "#DB960D" }} />)}</span>
);

// Lines from each dot centre to the nearest point on its card, measured in px.
const Connectors = ({ boxRef }) => {
  const [geo, setGeo] = useState(null);
  useEffect(() => {
    const box = boxRef.current;
    const measure = () => {
      const w = box.offsetWidth;
      const h = box.offsetHeight;
      const lines = SPOTS.map((s, i) => {
        const c = box.querySelector(`[data-testid="review-card-${i}"]`);
        const x1 = (s.dot[0] / 100) * w;
        const y1 = (s.dot[1] / 100) * h;
        const l = c.offsetLeft, t = c.offsetTop, r = l + c.offsetWidth, b = t + c.offsetHeight;
        const mids = [[l, (t + b) / 2], [r, (t + b) / 2], [(l + r) / 2, t], [(l + r) / 2, b]];
        const [x2, y2] = mids.reduce((a, m) => (Math.hypot(m[0] - x1, m[1] - y1) < Math.hypot(a[0] - x1, a[1] - y1) ? m : a));
        return { x1, y1, x2, y2 };
      });
      setGeo({ w, h, lines });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    box.querySelectorAll('[data-testid^="review-card-"]').forEach((c) => ro.observe(c));
    window.addEventListener("load", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("load", measure);
    };
  }, [boxRef]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!geo || !geo.w) return null;
  return (
    <svg className="absolute inset-0 w-full h-full hidden lg:block pointer-events-none" viewBox={`0 0 ${geo.w} ${geo.h}`} aria-hidden>
      {geo.lines.map((l, i) => (
        <motion.line
          key={i}
          x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke="rgba(255,255,255,0.8)" strokeWidth="1.25"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.6, ease, delay: 0.3 + i * 0.2 }}
        />
      ))}
    </svg>
  );
};

const Hotspot = ({ x, y, delay, className = "" }) => (
  <span className={`absolute -translate-x-1/2 -translate-y-1/2 ${className}`} style={{ left: `${x}%`, top: `${y}%` }}>
    <motion.span
      initial={{ scale: 0, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease, delay }}
      className="relative grid place-items-center w-5 h-5 lg:w-7 lg:h-7 rounded-full bg-[#ffffffe6] ring-4 ring-[#ffffff33]"
    >
      <span className="absolute inset-0 rounded-full bg-[#ffffff66] animate-ping" />
      <span className="relative w-2 h-2 rounded-full bg-[#18181B]" />
    </motion.span>
  </span>
);

const Verified = () => {
  const { t } = useTranslation();
  return (
    <span data-testid="verified-badge" title={t("home.verifiedBuyer")} aria-label={t("home.verifiedBuyer")} className="inline-flex">
      <BadgeCheck className="w-4 h-4" style={{ fill: "#DB960D", color: "#ffffff" }} strokeWidth={2.25} />
    </span>
  );
};

// Square thumb that matches the height of the text next to it.
const useSquareToText = () => {
  const ref = useRef(null);
  const [size, setSize] = useState(64);
  useEffect(() => {
    const el = ref.current;
    const ro = new ResizeObserver(() => {
      const h = Math.round(el.offsetHeight);
      setSize((s) => (h && Math.abs(s - h) > 1 ? h : s));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
};

const ReviewCard = ({ r, img, className = "", style, delay = 0, testid }) => {
  const [textRef, size] = useSquareToText();
  return (
  <motion.figure
    data-testid={testid}
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease, delay }}
    whileHover={{ y: -4 }}
    className={`flex gap-3 p-3 pr-4 rounded-2xl bg-[#ffffffdb] backdrop-blur-xl ring-1 ring-[#ffffff80] text-[#18181B] ${className}`}
    style={style}
  >
    <span className="relative shrink-0 rounded-xl overflow-hidden bg-[#0A0A0A]" style={{ width: size, height: size }}>
      <img src={img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
    </span>
    <div ref={textRef} className="min-w-0 self-start">
      <Stars />
      <blockquote className="mt-1.5 text-[12px] leading-snug text-[#3F3F46]">“{r.q}”</blockquote>
      <figcaption className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-[#18181B]">{r.n}<Verified /></figcaption>
    </div>
  </motion.figure>
  );
};

const Intro = () => {
  const { t } = useTranslation();
  return (
    <div className="text-[#F4F4F5]">
      <h2 className="font-display text-3xl md:text-4xl xl:text-5xl leading-[1.08] tracking-tight">
        <span className="font-light text-[#D4D4D8]">“{t("home.revHeadA")}</span>
        <br />
        <span className="font-semibold">{t("home.revHeadB")}”</span>
      </h2>
      <div className="mt-5"><Stars size="w-5 h-5" /></div>
      <p className="mt-4 max-w-xs text-sm md:text-base leading-relaxed text-[#D4D4D8]">{t("home.revBody")}</p>
    </div>
  );
};

const Badge = ({ names }) => {
  const { t } = useTranslation();
  return (
    <div data-testid="reviews-rating" className="inline-flex items-center gap-3 rounded-full bg-[#ffffff14] backdrop-blur-md ring-1 ring-[#ffffff26] p-1.5 pr-5">
      <span className="flex -space-x-2">
        {names.map((n, i) => (
          <span key={i} className="grid place-items-center w-8 h-8 rounded-full ring-2 ring-[#0A0A0A] text-[11px] font-semibold uppercase" style={{ background: ["#DB960D", "#F4F4F5", "#52525B"][i % 3], color: i % 3 === 2 ? "#F4F4F5" : "#0A0A0A" }}>{n[0]}</span>
        ))}
      </span>
      <span className="text-sm text-[#F4F4F5]"><span className="font-semibold">4.9</span><span className="text-[#A1A1AA]"> · {t("home.verifiedBuyers")}</span></span>
    </div>
  );
};

export const ReviewHouse = ({ reviews }) => {
  const boxRef = useRef(null);
  return (
  <Reveal y={20}>
    <div data-testid="review-house" className="relative overflow-hidden rounded-[1.75rem] bg-[#0A0A0A] ring-1 ring-black/10">
      <div className="lg:hidden px-6 pt-10 pb-6"><Intro /></div>
      <div ref={boxRef} className="relative aspect-[1264/848] lg:aspect-[1264/720]">
        <img src="/review-house.webp" alt="Smart home met KANN accessoires" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 hidden lg:block" style={{ background: "linear-gradient(90deg, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.35) 28%, rgba(10,10,10,0) 45%)" }} />
        <Connectors key={SPOTS.map((p) => p.dot.concat(p.card).join()).join("|")} boxRef={boxRef} />
        {SPOTS.map((s, i) => <Hotspot key={i} x={s.dot[0]} y={s.dot[1]} delay={0.2 + i * 0.2} className="hidden lg:block" />)}
        {SPOTS.map((s, i) => <Hotspot key={`m${i}`} x={s.dotM[0]} y={s.dotM[1]} delay={0.2 + i * 0.2} className="block lg:hidden" />)}
        <div className="absolute left-[5%] top-[10%] hidden lg:block"><Intro /></div>
        {SPOTS.map((s, i) => (
          <ReviewCard
            key={i}
            testid={`review-card-${i}`}
            r={reviews[i]}
            img={s.img}
            delay={0.5 + i * 0.2}
            className="absolute hidden lg:flex w-[23%]"
            style={{ left: `${s.card[0]}%`, top: `${s.card[1]}%` }}
          />
        ))}
        <div className="absolute left-[5%] bottom-[8%] hidden lg:block"><Badge names={reviews.map((r) => r.n)} /></div>
      </div>
      <div className="lg:hidden flex flex-col gap-3 p-4">
        {SPOTS.map((s, i) => <ReviewCard key={i} testid={`review-card-m-${i}`} r={reviews[i]} img={s.img} delay={i * 0.1} />)}
        <div className="pt-3 pb-2"><Badge names={reviews.map((r) => r.n)} /></div>
      </div>
    </div>
  </Reveal>
);
};
